import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React from "react";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    image = "image",
    buttonTitle = "buttonTitle",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        path1: LinkFieldValue;
        [fields.title]: string;
        [fields.image]: string;
        [fields.buttonTitle]: string;
        [fields.customCSS]: string;
    };
}

export interface CustomBannerWithButtonStyles {
    container?: GridContainerProps;
    linkStyle?: LinkPresentationProps;
    wrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    titleStyle?: InjectableCss;
    fieldsGridItem?: GridItemProps;
    bannerImage?: LazyImageProps;
}

export const customBannerWithButtonStyles: CustomBannerWithButtonStyles = {
    wrapper: {
        css: css`
            margin: 40px 0px;
        `
    },
    container: {
        css: css`
            background: ${getColor("primary.main")};
            width: 100%;
            border-radius: 6px;
            margin: 0 auto;

        `,
    },
    buttonWrapper: {
        css: css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 100%;
            margin-left: 30px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: 0 auto;
            img {
                width: 570px !important;
                height: 265px !important;
            }
        `,
    },
    linkStyle: {
        css: css`
            span {
                background-color: transparent !important;
                color: ${getColor("primary.main")} !important;
                font-size: 14px;
                font-weight: bold;
                line-height: normal;
                text-transform: uppercase;
                padding: 10px 0px;
            }
        `
    },
    linkWrapper: {
        css: css`
            background-color: ${getColor("primary.contrast")} !important;
            margin-top: 28px;
            text-align: center;
        `
    },
    titleStyle: {
        css: css`
            font-size: 26px;
            font-weight: bold;
            text-transform: uppercase;
        `
    },
    fieldsGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            height: 300px;
        `
    },
    bannerImage: {
        height: "250px",
        css: css`
            border-radius: 6px;
        `
    }
}

const Wrapper = styled.div<{}>`
    width: 70%;
    height: 100%;
    position: relative;
    color: white;
`;
const styles = customBannerWithButtonStyles;

const CustomBannerWithButton: React.FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.path1);
    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.fieldsGridItem}>
                    <Wrapper>
                        <StyledWrapper {...styles.buttonWrapper}>
                            <Typography {...styles.titleStyle}>
                                {fields?.title}
                            </Typography>
                            <StyledWrapper {...styles.linkWrapper}>
                                <Link href={url} {...styles.linkStyle}>
                                    {fields?.buttonTitle}
                                </Link>
                            </StyledWrapper>
                        </StyledWrapper>
                    </Wrapper>
                </GridItem>
                <GridItem {...styles.fieldsGridItem}>
                    <Wrapper>
                        <StyledWrapper {...styles.buttonWrapper}>
                            <StyledWrapper {...styles.imageWrapper} {...customCssWrapper}>
                                <LazyImage src={fields.image} altText={fields?.title} {...styles.bannerImage}/>
                            </StyledWrapper>
                        </StyledWrapper>
                    </Wrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};
const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};
const defaultCustomCss = `& img {
    width: 570px !important;
    height: 265px !important;
}`;
const widgetModule: WidgetModule = {
    component: CustomBannerWithButton,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
                fieldType: "Translatable",
            },
            {
                fieldType: "General",
                name: fields.image,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: "path1",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.buttonTitle,
                displayName: "buttonTitle",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
                fieldType: "Translatable",
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;